import type {
  AssigneeModel,
  EntityOrSuggestionRef,
  WhoElseDoesThisItem,
  WhoElseDoesThisItemPopulated
} from '@bliks-io/contracts';
import { getEntityRefId } from '@/lib/entityRefUtils';

function toWhoElseIdItem(i: WhoElseDoesThisItemPopulated): WhoElseDoesThisItem {
  return { entity: i.entity, model: i.model, status: i.status };
}

/**
 * Person ids to exclude from the "who else does this" picker. Only a User
 * assignee overlaps with the picker's User options —
 * OrgFunction/System/ExternalContact assignees never appear there, so nothing is
 * excluded for them.
 */
export function whoElseAssigneeExcludeIds(assignee: {
  id: string | undefined;
  model: AssigneeModel | undefined;
}): string[] {
  if (assignee.model !== 'User' || !assignee.id) return [];
  return [assignee.id];
}

// Strips the new user assignee from whoElseDoesThis before a patch goes out,
// so promoting a "who else" user to assignee automatically removes the duplicate.
export function syncWhoElseOnAssigneeChange<
  P extends {
    assignedTo?: EntityOrSuggestionRef | null;
    assignedToModel?: AssigneeModel;
    whoElseDoesThis?: WhoElseDoesThisItem[];
  }
>(patch: P, currentWhoElse: WhoElseDoesThisItemPopulated[] | undefined): P {
  if (patch.assignedToModel !== 'User' || !patch.assignedTo) return patch;
  // If the caller is itself editing whoElseDoesThis in the same patch, trust that value.
  if (patch.whoElseDoesThis !== undefined) return patch;

  const current = currentWhoElse ?? [];
  // A User assignee is always an entity ref (suggestions are orgFunctions/systems only).
  const newAssigneeId = patch.assignedTo.type === 'entity' ? patch.assignedTo.id : undefined;
  if (!newAssigneeId) return patch;
  const hasAssignee = current.some(
    (i) => i.model === 'User' && getEntityRefId(i.entity) === newAssigneeId
  );
  if (!hasAssignee) return patch;

  const filtered: WhoElseDoesThisItem[] = current
    .filter((i) => !(i.model === 'User' && getEntityRefId(i.entity) === newAssigneeId))
    .map(toWhoElseIdItem);

  return { ...patch, whoElseDoesThis: filtered };
}
